"use client";

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useFluidSettings } from "./widgetSettings";

interface ShadowDOMWrapperProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  mode?: "open" | "closed";
  /**
   * Optional ref to access the Shadow DOM root.
   * Useful for portals that need to render inside the shadow root.
   */
  shadowRootRef?: React.RefObject<ShadowRoot | null>;
  /**
   * Optional prop to explicitly pass a primary color.
   * Takes precedence over the color from useFluidSettings if provided.
   *
   * - `null`: Explicitly disables the primary color, resulting in no color being applied.
   * - `undefined`: Falls back to the default color from useFluidSettings.
   */
  primaryColor?: string | null;
}

// Define window with our global state props
interface FluidWindow extends Window {
  __FLUID_STYLE_TEXT?: string;
  __FLUID_STYLE_URL?: string;
  __FLUID_SHADOW_STYLESHEET?: CSSStyleSheet;
  __FLUID_SHADOW_STYLESHEET_LOADING?: boolean;
}

/**
 * ShadowDOMWrapper component
 *
 * Wraps any content in a Shadow DOM for style encapsulation.
 * Uses React 19's improved Shadow DOM integration.
 * Uses constructable stylesheets for modern browsers for better performance.
 * Automatically applies company colors from Fluid settings.
 */
export const ShadowDOMWrapper: React.FC<ShadowDOMWrapperProps> = ({
  children,
  className,
  style,
  mode = "open",
  shadowRootRef,
  primaryColor: explicitPrimaryColor,
}) => {
  // Skip rendering the Shadow DOM during server-side rendering
  const isServer = typeof window === "undefined";

  // If we're on the server, render a simple container without shadow DOM
  if (isServer) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }

  // Client-side code starts here
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [shadowRoot, setShadowRoot] = useState<ShadowRoot | null>(null);

  // Use the shared settings hook to get primary color
  const { applyCompanyColorToShadowDOM, getDisplayNames } = useFluidSettings();
  const { primaryColor: fluidPrimaryColor } = getDisplayNames();

  // Use explicit color passed as prop, or fall back to Fluid settings
  const effectivePrimaryColor = explicitPrimaryColor ?? fluidPrimaryColor;

  // Creates and manages the shadow root
  useEffect(() => {
    const fluidWindow = window as unknown as FluidWindow;

    if (containerRef.current && !shadowRoot) {
      // Create shadow root
      const root = containerRef.current.attachShadow({ mode });

      // Store shadow root in state and in the ref if provided
      setShadowRoot(root);
      if (shadowRootRef) {
        shadowRootRef.current = root;
      }

      // Function to set up styles in the shadow root
      const setupStyles = async () => {
        // Check if browser supports Constructable StyleSheets with more robust detection
        const supportsConstructableStyleSheets = (() => {
          try {
            // Check for basic API support
            if (
              !("adoptedStyleSheets" in Document.prototype) ||
              !("adoptedStyleSheets" in ShadowRoot.prototype) ||
              typeof CSSStyleSheet === "undefined"
            ) {
              return false;
            }

            // Test if we can actually create a CSSStyleSheet
            const testSheet = new CSSStyleSheet();
            if (typeof testSheet.replaceSync !== "function") {
              return false;
            }

            // Test if we can actually use adoptedStyleSheets
            testSheet.replaceSync("/* test */");
            const testDiv = document.createElement("div");
            const testShadow = testDiv.attachShadow({ mode: "open" });
            testShadow.adoptedStyleSheets = [testSheet];

            return true;
          } catch {
            return false;
          }
        })();

        if (supportsConstructableStyleSheets) {
          // Modern approach: Use Constructable StyleSheets API
          try {
            // Re-use cached stylesheet if available
            if (fluidWindow.__FLUID_SHADOW_STYLESHEET) {
              root.adoptedStyleSheets = [fluidWindow.__FLUID_SHADOW_STYLESHEET];
              return;
            }

            // Create new stylesheet if we have CSS text
            if (fluidWindow.__FLUID_STYLE_TEXT) {
              const stylesheet = new CSSStyleSheet();
              stylesheet.replaceSync(fluidWindow.__FLUID_STYLE_TEXT);

              // Cache for reuse
              fluidWindow.__FLUID_SHADOW_STYLESHEET = stylesheet;

              // Apply to shadow root
              root.adoptedStyleSheets = [stylesheet];
              return;
            }

            // Fetch CSS if we have a URL
            if (fluidWindow.__FLUID_STYLE_URL) {
              try {
                const response = await fetch(fluidWindow.__FLUID_STYLE_URL);
                if (response.ok) {
                  const cssText = await response.text();

                  // Cache the CSS text
                  fluidWindow.__FLUID_STYLE_TEXT = cssText;

                  // Create stylesheet
                  const stylesheet = new CSSStyleSheet();
                  stylesheet.replaceSync(cssText);

                  // Cache for reuse
                  fluidWindow.__FLUID_SHADOW_STYLESHEET = stylesheet;

                  // Apply to shadow root
                  root.adoptedStyleSheets = [stylesheet];
                  return;
                }
              } catch (error) {
                console.error("Error fetching styles:", error);
                // Fall through to legacy approach
              }
            }
          } catch (error) {
            console.error("Error applying constructable stylesheet:", error);
            // Fall through to legacy approach
          }
        }

        // Legacy fallback: Use style or link element
        if (fluidWindow.__FLUID_STYLE_TEXT) {
          const styleEl = document.createElement("style");
          styleEl.textContent = fluidWindow.__FLUID_STYLE_TEXT;
          root.appendChild(styleEl);
        } else if (fluidWindow.__FLUID_STYLE_URL) {
          const linkEl = document.createElement("link");
          linkEl.rel = "stylesheet";
          linkEl.href = fluidWindow.__FLUID_STYLE_URL;
          root.appendChild(linkEl);
        }
      };

      // Execute the style setup
      void setupStyles();
    }
  }, [mode, shadowRoot, shadowRootRef]);

  // Apply Fluid primary color to shadow DOM when it's available
  useEffect(() => {
    if (
      shadowRoot &&
      effectivePrimaryColor &&
      effectivePrimaryColor.trim() !== ""
    ) {
      // Apply color using the utility function from the settings hook
      applyCompanyColorToShadowDOM(shadowRoot, effectivePrimaryColor);
    }
  }, [shadowRoot, effectivePrimaryColor, applyCompanyColorToShadowDOM]);

  // Create a div element with the provided class and style
  return (
    <div
      ref={containerRef}
      className={className}
      style={style}
      data-fluid-shadow-root-container="true"
    >
      {/* Only render content when shadow root is ready */}
      {shadowRoot && createPortal(children, shadowRoot)}
    </div>
  );
};

export default ShadowDOMWrapper;
